import React, { useMemo, useRef } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { ChartData } from '../services/chartDataGenerator';

interface MobileChartProps {
  chartData: ChartData;
  hiddenSeries: Set<string>;
  onSeriesToggle: (seriesName: string) => void;
}

const MobileChart: React.FC<MobileChartProps> = ({ chartData, hiddenSeries, onSeriesToggle }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Note: We removed horizontal scrolling and width measurement
  // ResponsiveContainer uses 100% width of its parent automatically
  // Helper function to extract unit from series
  const getUnit = (s: any) => {
    if (s.unit) return s.unit;
    // Try to extract from name format "Label (TagId) (Unit)"
    const unitMatch = s.name.match(/\([^)]+\)\s*\(([^)]+)\)$/);
    if (unitMatch) return unitMatch[1];
    // Try format "Label (TagId)" - extract unit from tag ID
    const tagIdMatch = s.name.match(/\(([A-Z0-9]+)\)/);
    if (tagIdMatch) {
      const tagId = tagIdMatch[1];
      if (tagId.includes('Percent')) return '%';
      if (tagId.includes('Energy')) return 'kWh';
      if (tagId.includes('Power')) return 'W';
      if (tagId.includes('Temp')) return '°C';
      if (tagId.includes('Tariff')) return 'ct/kWh';
    }
    return '';
  };

  // Get distinct units to determine if we need multiple Y-axes or multiple charts
  const allUnits = chartData.series.map(getUnit).filter(u => u);
  const distinctUnits = Array.from(new Set(allUnits));
  const hasMultipleUnits = distinctUnits.length > 1;
  
  // Group series by unit for mobile multi-chart rendering
  const seriesByUnit = useMemo(() => {
    const grouped: { [unit: string]: typeof chartData.series } = {};
    chartData.series.forEach(series => {
      const unit = getUnit(series);
      if (!grouped[unit]) {
        grouped[unit] = [];
      }
      grouped[unit].push(series);
    });
    return grouped;
  }, [chartData]);

  // Calculate domain for each unit type - use undefined to let Recharts auto-calculate
  // Only set explicit domain for percentages
  const getDomain = (unit: string): [number, number] | undefined => {
    if (unit === '%') return [0, 100];
    // Let Recharts auto-calculate domain for other units - return undefined
    return undefined;
  };

  // Prepare data for Recharts - convert to array of objects
  // VALIDATION: Ensure data integrity
  const chartDataArray = useMemo(() => {
    // VALIDATION: Ensure timeLabels and series data lengths match
    const maxLength = Math.max(
      chartData.timeLabels.length,
      ...chartData.series.map(s => s.data.length)
    );
    if (chartData.timeLabels.length !== maxLength) {
      console.warn('[MobileChart] timeLabels length mismatch:', {
        timeLabelsLength: chartData.timeLabels.length,
        maxSeriesLength: maxLength
      });
    }
    
    return chartData.timeLabels.map((label, index) => {
      const dataPoint: any = { timeLabel: label };
      chartData.series.forEach((series) => {
        // VALIDATION: Ensure index is within bounds
        if (index >= series.data.length) {
          console.warn('[MobileChart] Data index out of bounds:', {
            seriesName: series.name,
            index,
            dataLength: series.data.length
          });
        }
        // Convert NaN to null for proper handling
        const value = index < series.data.length 
          ? (isNaN(series.data[index]) ? null : series.data[index])
          : null;
        dataPoint[series.name] = value;
      });
      return dataPoint;
    });
  }, [chartData]);

  // For long time ranges, show fewer x-axis labels
  const shouldShowFewerLabels = chartData.timeLabels.length > 15;
  const xAxisTickInterval = shouldShowFewerLabels 
    ? Math.ceil(chartData.timeLabels.length / 8) 
    : 0;

  // Calculate dynamic height based on viewport - MAKE IT TALLER to see data better
  // Account for: header (~60px), message text (~50px), chart margins (~100px), padding (~20px)
  // Use MORE of the viewport height for charts
  const chartHeight = useMemo(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // For mobile (typically < 640px), use MORE height
    let calculatedHeight: number;
    if (viewportWidth < 640) {
      // Use 60% of viewport height for charts (much more than before)
      // Account for minimal overhead: header + message + padding
      const overhead = 150; // Reduced overhead
      const availableHeight = Math.max(viewportHeight - overhead, 400);
      // Use most of available height - make it TALLER
      calculatedHeight = Math.min(availableHeight, viewportHeight * 0.65);
    } else {
      // Desktop: can be larger
      calculatedHeight = Math.min(Math.max(viewportHeight * 0.55, 500), 600);
    }
    
    console.log('[MobileChart] Height calculation:', {
      viewportWidth,
      viewportHeight,
      isMobile: viewportWidth < 640,
      calculatedHeight,
      overhead: viewportWidth < 640 ? 150 : 'N/A'
    });
    
    return calculatedHeight;
  }, []);

  // Clean series name for display
  const getDisplayName = (name: string) => {
    const match = name.match(/^(.*?)\s*\([^)]+\)\s*\(([^)]+)\)$/);
    if (match && match.length > 2) {
      return `${match[1]} (${match[2]})`;
    }
    return name.replace(/\s*\([^)]+\)\s*\([^)]+\)$/, '').replace(/\s*\([^)]+\)$/, '');
  };

  // Custom tooltip for mobile - VALIDATED to ensure correct data
  // CRITICAL: Tooltip must be able to overflow container boundaries
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    // VALIDATION: Ensure label matches actual data
    const dataIndex = chartData.timeLabels.findIndex(tl => tl === label);
    if (dataIndex === -1) {
      console.warn('[MobileChart] Tooltip label not found in timeLabels:', label);
    }

    return (
      <div 
        className="bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-[9999]" 
        style={{ 
          minWidth: '180px',
          maxWidth: '250px',
          position: 'relative',
          pointerEvents: 'auto'
        }}
      >
        <p className="font-medium text-sm mb-2 text-gray-800 break-words">{label}</p>
        {payload.map((entry: any, index: number) => {
          if (hiddenSeries.has(entry.dataKey)) return null;
          const series = chartData.series.find(s => s.name === entry.dataKey);
          if (!series) {
            console.warn('[MobileChart] Series not found for tooltip:', entry.dataKey);
            return null;
          }
          
          // VALIDATION: Verify data value matches actual series data
          const dataIndex = chartData.timeLabels.findIndex(tl => tl === label);
          const actualValue = dataIndex >= 0 ? series.data[dataIndex] : null;
          const tooltipValue = entry.value;
          
          // Validate tooltip value matches actual data (allow for null/NaN handling)
          if (dataIndex >= 0 && actualValue !== null && !isNaN(actualValue)) {
            const valueDiff = Math.abs((tooltipValue || 0) - actualValue);
            if (valueDiff > 0.01) { // Allow small floating point differences
              console.warn('[MobileChart] Tooltip value mismatch:', {
                dataKey: entry.dataKey,
                tooltipValue,
                actualValue,
                label,
                dataIndex
              });
            }
          }
          
          const unit = series.unit || getUnit(series);
          const displayName = getDisplayName(entry.dataKey);
          const displayValue = tooltipValue !== null && tooltipValue !== undefined && !isNaN(tooltipValue)
            ? tooltipValue
            : (actualValue !== null && !isNaN(actualValue) ? actualValue : null);
          
          return (
            <p key={index} className="text-sm break-words" style={{ color: entry.color }}>
              <span className="font-medium">{displayName}:</span>{' '}
              {displayValue !== null 
                ? `${displayValue} ${unit}` 
                : 'N/A'}
            </p>
          );
        })}
      </div>
    );
  };

  // Render chart for a specific unit group
  const renderChartForUnit = (unit: string, unitSeries: typeof chartData.series, chartHeight: number) => {
    const commonProps = {
      data: chartDataArray,
      // CRITICAL: MAXIMUM width optimization - absolute minimal margins to prevent cutoff
      // Top: minimal space for data points at max values
      // Right: minimal space for data points at right edge (CRITICAL - prevent cutoff)
      // Left: minimal space for Y-axis labels and ticks
      // Bottom: space for rotated X-axis labels
      margin: { top: 8, right: 12, left: 30, bottom: 70 },
    };

    // Filter visible series for this unit
    const visibleSeries = unitSeries.filter(s => !hiddenSeries.has(s.name));

    if (chartData.chartType === 'bar') {
      return (
        <BarChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="timeLabel"
            angle={chartData.timeLabels.length > 8 ? -45 : 0}
            textAnchor={chartData.timeLabels.length > 8 ? 'end' : 'middle'}
            height={chartData.timeLabels.length > 8 ? 80 : 60}
            interval={xAxisTickInterval}
            tick={{ fontSize: 11 }}
            style={{ fontSize: '11px' }}
            tickFormatter={(value) => {
              // VALIDATION: Ensure value exists in timeLabels
              if (!chartData.timeLabels.includes(value)) {
                console.warn('[MobileChart] X-axis label not found in timeLabels:', value);
              }
              // For mobile, show shorter format
              const timeMatch = value.match(/(\d{2}:\d{2})/);
              if (timeMatch) return timeMatch[1];
              // If it's a date, show just day/month
              const dateMatch = value.match(/(\d{2})-(\d{2})/);
              if (dateMatch) return `${dateMatch[2]}/${dateMatch[1]}`;
              return value.length > 10 ? value.substring(0, 10) : value;
            }}
          />
          <YAxis
            domain={getDomain(unit)}
            tick={{ fontSize: 11 }}
            width={32}
          />
          <Tooltip 
            content={<CustomTooltip />}
            allowEscapeViewBox={{ x: true, y: true }}
            wrapperStyle={{ zIndex: 9999 }}
          />
          {visibleSeries.map((series) => (
            <Bar
              key={series.name}
              dataKey={series.name}
              fill={series.color}
            />
          ))}
        </BarChart>
      );
    }

    if (chartData.chartType === 'area') {
      return (
        <AreaChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="timeLabel"
            angle={chartData.timeLabels.length > 8 ? -45 : 0}
            textAnchor={chartData.timeLabels.length > 8 ? 'end' : 'middle'}
            height={chartData.timeLabels.length > 8 ? 80 : 60}
            interval={xAxisTickInterval}
            tick={{ fontSize: 11 }}
            style={{ fontSize: '11px' }}
            tickFormatter={(value) => {
              // VALIDATION: Ensure value exists in timeLabels
              if (!chartData.timeLabels.includes(value)) {
                console.warn('[MobileChart] X-axis label not found in timeLabels:', value);
              }
              // For mobile, show shorter format
              const timeMatch = value.match(/(\d{2}:\d{2})/);
              if (timeMatch) return timeMatch[1];
              // If it's a date, show just day/month
              const dateMatch = value.match(/(\d{2})-(\d{2})/);
              if (dateMatch) return `${dateMatch[2]}/${dateMatch[1]}`;
              return value.length > 10 ? value.substring(0, 10) : value;
            }}
          />
          <YAxis
            domain={getDomain(unit)}
            tick={{ fontSize: 11 }}
            width={32}
          />
          <Tooltip 
            content={<CustomTooltip />}
            allowEscapeViewBox={{ x: true, y: true }}
            wrapperStyle={{ zIndex: 9999 }}
          />
          {visibleSeries.map((series) => (
            <Area
              key={series.name}
              type="monotone"
              dataKey={series.name}
              stroke={series.color}
              fill={series.color}
              fillOpacity={0.2}
              connectNulls={false}
            />
          ))}
        </AreaChart>
      );
    }

    // Default to line chart
    return (
      <LineChart {...commonProps}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="timeLabel"
          angle={chartData.timeLabels.length > 8 ? -45 : 0}
          textAnchor={chartData.timeLabels.length > 8 ? 'end' : 'middle'}
          height={chartData.timeLabels.length > 8 ? 80 : 60}
          interval={xAxisTickInterval}
          tick={{ fontSize: 11 }}
          style={{ fontSize: '11px' }}
          tickFormatter={(value) => {
            const timeMatch = value.match(/(\d{2}:\d{2})/);
            if (timeMatch) return timeMatch[1];
            const dateMatch = value.match(/(\d{2})-(\d{2})/);
            if (dateMatch) return `${dateMatch[2]}/${dateMatch[1]}`;
            return value.length > 10 ? value.substring(0, 10) : value;
          }}
        />
          <YAxis
            domain={getDomain(unit)}
            tick={{ fontSize: 11 }}
            width={32}
          />
        <Tooltip content={<CustomTooltip />} />
        {visibleSeries.map((series) => {
          // Determine if we should smooth the line (only if we have dense data)
          const validDataPoints = series.data.filter(v => v !== null && !isNaN(v)).length;
          const shouldSmooth = validDataPoints / series.data.length > 0.5;
          
          return (
            <Line
              key={series.name}
              type={shouldSmooth ? 'monotone' : 'linear'}
              dataKey={series.name}
              stroke={series.color}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              connectNulls={false}
            />
          );
        })}
      </LineChart>
    );
  };

  // Render single chart (original behavior for single unit or desktop)
  const renderChart = () => {
    return renderChartForUnit(distinctUnits[0] || '', chartData.series, chartHeight);
  };

  // For mobile with multiple units, split into separate charts
  // Each chart gets a reduced height to fit multiple charts on screen
  const shouldSplitCharts = hasMultipleUnits;
  const chartsPerUnit = shouldSplitCharts ? Object.keys(seriesByUnit).length : 1;
  const individualChartHeight = shouldSplitCharts 
    ? Math.max(220, Math.floor(chartHeight / chartsPerUnit))
    : chartHeight;
  
  // NO HORIZONTAL SCROLLING - always fit in container width
  // Use actual container width, not window width (container might be constrained by parent)

  // Note: Width measurement removed - ResponsiveContainer handles sizing automatically

  // If multiple units, render separate charts stacked vertically
  if (shouldSplitCharts) {
    const totalHeight = individualChartHeight * chartsPerUnit;
    return (
      <div 
        ref={containerRef}
        data-chart-container
        style={{ 
          width: '100%', 
          height: `${totalHeight}px`,
          minHeight: `${totalHeight}px`,
          maxWidth: '100%',
          overflow: 'visible',
          position: 'relative',
          boxSizing: 'border-box',
          // CRITICAL: Allow tooltips to overflow, NO padding to maximize width
          paddingRight: '0px',
          paddingLeft: '0px'
        }}
      >
        {Object.entries(seriesByUnit).map(([unit, unitSeries], index) => {
          // Get series names for this unit (for display in header)
          const visibleUnitSeries = unitSeries.filter(s => !hiddenSeries.has(s.name));
          const seriesNames = visibleUnitSeries.map(s => getDisplayName(s.name)).join(', ');
          
          return (
          <div 
            key={unit}
            style={{
              width: '100%',
              height: `${individualChartHeight}px`,
              minHeight: `${individualChartHeight}px`,
              marginBottom: index < chartsPerUnit - 1 ? '8px' : '0'
            }}
          >
            {/* Series names - shown as header above chart */}
            <div className="text-sm font-semibold text-gray-700 mb-2 px-2 text-center">
              {seriesNames || 'Data'}
            </div>
            {/* NO HORIZONTAL SCROLL - always fit on screen */}
            <div style={{ width: '100%', height: `${individualChartHeight}px`, padding: '0', overflow: 'visible' }}>
              <ResponsiveContainer 
                width="100%" 
                height="100%"
              >
                {renderChartForUnit(unit, unitSeries, individualChartHeight)}
              </ResponsiveContainer>
            </div>
          </div>
          );
        })}
      </div>
    );
  }

  // Single chart (original behavior)
  return (
    <div 
      ref={containerRef}
      data-chart-container
      style={{ 
        width: '100%', 
        height: `${chartHeight}px`,
        minHeight: `${chartHeight}px`,
        maxWidth: '100%',
        overflow: 'visible',
        position: 'relative',
        boxSizing: 'border-box',
        // CRITICAL: Allow tooltips to overflow
        paddingRight: '20px',
        paddingLeft: '5px'
      }}
    >
      {/* NO HORIZONTAL SCROLL - always fit on screen */}
      <div style={{ width: '100%', height: `${chartHeight}px`, padding: '0', overflow: 'visible' }}>
        <ResponsiveContainer 
          width="100%" 
          height="100%"
        >
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MobileChart;

